import { useMemo } from "react";
import { Col, Card } from "antd";

import { Guides } from "graphql/generated/graphql";
import { GuideStatus } from "constants/field-values";
import { getGuideProductPhotos, guideReadTimeCalculator } from "utils/helpers";
import { useVideoThumbnail } from "hooks/useVideoThumbnail";

// Placeholder images for example purposes
const mainImage = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80";

// Function to count distinct property_ids across all installed_products in product_guides
const countDistinctProperties = (guide: Guides) => {
	const distinctPropertyIds = new Set<string>();

	guide.product_guides.forEach(productGuide => {
		if (productGuide.product?.installed_products) {
			productGuide.product.installed_products.forEach(installedProduct => {
				if (installedProduct.property_id) {
					distinctPropertyIds.add(installedProduct.property_id);
				}
			});
		}
	});

	return distinctPropertyIds.size;
};

interface GuideCardProps {
	onClick: () => void;
	guide: Guides;
}

const DRAFT_TEXT = "READY TO REVIEW AND PUBLISH";
const PROCESSING_TEXT = "GENERATING YOUR GUIDE";

const GuideCard = ({ onClick, guide }: GuideCardProps) => {
	const videoThumbnail = guide.thumbnail_url ? guide.thumbnail_url : useVideoThumbnail(guide.video_url);

	const estimatedReadTime = useMemo(() => guideReadTimeCalculator(guide.guide_steps), [guide.guide_steps]);
	const propertiesCount = useMemo(() => countDistinctProperties(guide), [guide]);
	const stepsCount = guide.guide_steps.length;
	const updatedDate = new Date(guide.updated_at).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric"
	});
	const photoLinks = getGuideProductPhotos(guide);

	const guideHeader = useMemo(() => {
		let overlayText = "";
		let fallbackElement = <img className="guide-card-main-image" src={mainImage} alt="Main" />;

		switch (guide.status) {
			case GuideStatus.processing:
				overlayText = PROCESSING_TEXT;
				fallbackElement = (
					<div className="guide-card-generating-guide-placeholder">
						<span>{PROCESSING_TEXT}</span>
					</div>
				);
				break;

			case GuideStatus.draft:
				overlayText = DRAFT_TEXT;
				fallbackElement = (
					<>
						<img className="guide-card-main-image-draft" src={mainImage} alt="Main" />
						<span>{DRAFT_TEXT}</span>
					</>
				);
				break;
			default:
				break;
		}

		if (guide.video_url) {
			const thumbnailToUse = videoThumbnail || mainImage;

			return (
				<div className="guide-card-video-thumbnail-container">
					<img className="guide-card-main-image" src={thumbnailToUse} alt="Video thumbnail" />
					{overlayText ? (
						<div className="guide-card-text-overlay">
							<span>{overlayText}</span>
						</div>
					) : null}
				</div>
			);
		} else {
			return fallbackElement;
		}
	}, [guide, videoThumbnail]);

	return (
		<Col className="guide-card-col">
			<Card hoverable bordered={false} className="guide-card" onClick={onClick}>
				{/* Top images */}
				<div className="guide-card-image-section">
					{guideHeader}
					<div className="guide-card-thumb-images">
						{photoLinks.map((src, idx) => (
							<img key={idx} src={src} alt={`Thumb ${idx + 1}`} className="guide-card-thumb" />
						))}
					</div>
				</div>
				{/* Card content */}
				<div className="guide-card-content-inner-container">
					<div className="guide-card-properties-count">{`${propertiesCount} ${
						propertiesCount === 1 ? "PROPERTY" : "PROPERTIES"
					}`}</div>
					<h3 className="guide-card-title">{guide.title}</h3>
					<div className="guide-card-info-row">
						<span>
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/compass.svg`} alt="steps" />{" "}
							{`${stepsCount} ${stepsCount > 1 ? "steps" : "step"}`}
						</span>
						<span>
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/stopwatch.svg`} alt="duration" />{" "}
							{`${estimatedReadTime} ${estimatedReadTime > 1 ? "minutes" : "minute"}`}
						</span>
						<span>
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/calendar.svg`} alt="calendar" /> {updatedDate}
						</span>
					</div>
					<div className="guide-card-description">{guide.description}</div>
				</div>
			</Card>
		</Col>
	);
};

export default GuideCard;
